<template>
  <div class="war-room-post-vote">
    <WarRoomVoteButton
      class="war-room-post-vote__button"
      :is-upvoted="isUpvoted"
      :is-upvote="true"
      @click="handleUpVote"
    >
      <ArrowIcon class="war-room-post-vote__icon" />
    </WarRoomVoteButton>
    <div :class="counterClasses" data-test="war-room-post-votes-counter">
      {{ post.rating }}
    </div>
    <WarRoomVoteButton
      class="war-room-post-vote__button"
      :is-downvoted="isDownvoted"
      :is-downvote="true"
      @click="handleDownVote"
    >
      <ArrowIcon :class="arrowIconClassesDownvoted" />
    </WarRoomVoteButton>
  </div>
</template>

<script lang="ts">
import { defineComponent } from '@nuxtjs/composition-api';
import type { PropOptions } from 'vue';
import { mapActions } from 'vuex';
import WarRoomVoteButton from '@/components/partials/war-room/WarRoomVote/WarRoomVoteButton.vue';
import ArrowIcon from '@/components/partials/war-room/WarRoomVote/images/arrow.vue.svg';
import warRoomSignUpMixin from '@/components/partials/war-room/WarRoomSignUp/warRoomSignUpMixin';
import { votableTypes } from '@/components/partials/war-room/constants';
import type { IPost } from '@/types/community';

export default defineComponent({
  name: 'WarRoomPostVote',
  components: {
    ArrowIcon,
    WarRoomVoteButton,
  },
  mixins: [warRoomSignUpMixin],
  props: {
    post: {
      type: Object,
      required: true,
    } as PropOptions<IPost>,
  },
  computed: {
    arrowIconClassesDownvoted() {
      return {
        'war-room-post-vote__icon': true,
        'war-room-post-vote__icon--reversed': true,
      };
    },
    isUpvoted() {
      return this.post?.current_vote === 1;
    },
    isDownvoted() {
      return this.post?.current_vote === -1;
    },
    counterClasses() {
      return {
        'war-room-post-vote__count': true,
        'war-room-post-vote__count--upvoted': this.isUpvoted,
        'war-room-post-vote__count--downvoted': this.isDownvoted,
      };
    },
  },
  methods: {
    ...mapActions('warRoom', ['addVote', 'removeVote']),
    handleUpVote() {
      if (this.$auth.loggedIn) {
        if (this.isUpvoted) {
          this.handleRemoveVote();
        } else {
          this.handleAddVote(1);
        }
      } else {
        this.openWarRoomSignUpModal();
      }
    },
    handleDownVote() {
      if (this.$auth.loggedIn) {
        if (this.isDownvoted) {
          this.handleRemoveVote();
        } else {
          this.handleAddVote(-1);
        }
      } else {
        this.openWarRoomSignUpModal();
      }
    },
    handleAddVote(value: number) {
      this.$analytics.logEvent(`war-room__post__rating__set`, { value });
      this.addVote({ votable: this.post, type: votableTypes.POST, value });
    },
    handleRemoveVote() {
      this.$analytics.logEvent(`war-room__post__rating__unset`);
      this.removeVote({ votable: this.post, type: votableTypes.POST });
    },
  },
});
</script>

<style scoped lang="scss">
.war-room-post-vote {
  display: inline-flex;
  padding: 3px 8px;
  color: var(--theme-color-text);
  align-items: center;
  border-radius: 6px;
  border: 1px solid var(--theme-color-border);

  &__icon {
    width: 24px;
    height: 24px;
    color: inherit;

    &--reversed {
      transform: rotate(180deg);
    }
  }

  &__count {
    @include body-semibold;

    color: inherit;
    margin: 0 6px;

    &--upvoted {
      color: var(--blue-60);
    }

    &--downvoted {
      color: var(--red-50);
    }
  }

  &__button {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
